/**
 * The Email screen — docs/modules/emailInbox.model.md. Every email sent from
 * the CRM (`GET /email/sent`), filterable, with whether it's been opened
 * (the tracking pixel `emailSend.service.js` embeds at enqueue time). Compose
 * goes through `RecipientPickerModal` first, since a send has no page of its
 * own to inherit an anchor from the way the Account/Lead/Contact composers do.
 */
import { useMemo, useState } from 'react';
import { PageHeader, Card, Badge, SearchInput, ChipFilter, MockBadge } from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { isMock } from '../../api/mockFallback.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useSentEmails } from '../emailTemplates/useEmailTemplates.js';
import { useAccountContacts } from '../accounts/useAccounts.js';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';
import { RecipientPickerModal } from './RecipientPickerModal.jsx';
import { dateTime } from '../../utils/formatters.js';

const STATUS_TONE = { queued: 'sky', sending: 'sky', sent: 'emerald', failed: 'rose', cancelled: 'gray' };

function OpenedMarker({ opened, openCount }) {
  if (!opened) return <span className="t-xs ink-4">Not opened</span>;
  return (
    <span className="t-xs" style={{ color: 'var(--emerald-600, #15803d)' }} title={`Opened ${openCount} time${openCount === 1 ? '' : 's'}`}>
      <i className="bi bi-eye-fill me-1" aria-hidden="true" />Opened
    </span>
  );
}

export default function EmailInboxPage() {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [opened, setOpened] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null); // { anchor, defaultEmail, accountId? }

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);

  const params = useMemo(
    () => ({
      ...pages.params,
      q: debounced || undefined,
      status: status || undefined,
      opened: opened || undefined,
    }),
    [pages.params, debounced, status, opened],
  );

  const { data, isLoading, error, refetch } = useSentEmails(params);
  const { data: accountContactsPage } = useAccountContacts(picked?.accountId, { limit: 50 });

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const relatedLabel = (r) => {
    if (r.lead_id) return 'Lead';
    if (r.account_id) return 'Account';
    if (r.contact_id) return 'Contact';
    if (r.opportunity_id) return 'Opportunity';
    if (r.campaign_id) return 'Campaign';
    return '—';
  };

  const columns = [
    { key: 'created_at', header: 'Sent', width: 160, render: (r) => <span className="t-xs">{dateTime(r.created_at)}</span> },
    {
      key: 'subject',
      header: 'Subject',
      render: (r) => <span className="t-sm fw-600">{r.subject || '(no subject)'}</span>,
    },
    { key: 'to_addresses', header: 'To', width: 220, render: (r) => <span className="t-xs">{r.to_addresses.join(', ')}</span> },
    { key: 'related', header: 'Linked to', width: 110, render: (r) => <span className="t-xs ink-3">{relatedLabel(r.related)}</span> },
    { key: 'status', header: 'Status', width: 100, render: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'gray'}>{r.status}</Badge> },
    { key: 'opened', header: 'Read', width: 110, render: (r) => <OpenedMarker opened={r.opened} openCount={r.open_count} /> },
    { key: 'owner', header: 'Sent by', width: 150, render: (r) => <span className="t-xs">{r.owner?.full_name ?? '—'}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Email"
        subtitle="Everything sent from the CRM, and whether it's been read."
        badge={isMock(data) ? <MockBadge section="email" /> : null}
      >
        {can('email', 'send') && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={() => setPickerOpen(true)}>
            <i className="bi bi-pencil-square me-1" aria-hidden="true" />Compose
          </button>
        )}
      </PageHeader>

      <Card bodyClass="p-0">
        <div className="filter-bar">
          <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search subject or a To address…" className="flex-grow-1" />
          <ChipFilter
            label="Status" value={status} onChange={onFilter(setStatus)}
            options={['queued', 'sending', 'sent', 'failed', 'cancelled']}
          />
          <ChipFilter
            label="Read" value={opened} onChange={onFilter(setOpened)}
            options={[{ value: 'true', label: 'Opened' }, { value: 'false', label: 'Not opened' }]}
          />
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          page={data?.page}
          onNext={pages.goNext}
          onPrevious={pages.goPrevious}
          canGoPrevious={pages.canGoPrevious}
          rowKey={(r) => r.activity_id}
          dense
          emptyTitle="No email sent yet"
        />
      </Card>

      <RecipientPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(selection) => { setPicked(selection); setPickerOpen(false); }}
      />

      <EmailComposerModal
        open={Boolean(picked)}
        onClose={() => setPicked(null)}
        anchor={picked?.anchor ?? {}}
        defaultEmail={picked?.defaultEmail ?? ''}
        contacts={picked?.accountId ? (accountContactsPage?.data ?? []) : []}
      />
    </>
  );
}
